import { PortablePTag } from '@otovo/shared/components/Sanity/PortableTextComponents';
import { Cloud$MainHardwareType } from '@otovo/shared/types/cloudEnums';
import { Sanity$HeroSection } from '@otovo/shared/types/sanityTypes';
import cx from 'classnames';
import { stegaClean } from 'next-sanity';
import HeroAddressSearchBar from './HeroAddressSearchBar';
import TrustVendor from './TrustVendor';

type Props = {
  heroSection: Sanity$HeroSection;
  mainHardwareType?: Cloud$MainHardwareType;
};

const DefaultContent = ({
  heroSection,
  mainHardwareType = 'pv_system',
}: Props) => {
  const { headline, lead, keyValuePropositions, keyValuePropositionTitle } =
    heroSection.defaultFields;
  const hasAddressBar = stegaClean(heroSection.addressBarToggle);

  return (
    <div className="isolate mx-4 mt-auto h-[25rem] w-full max-w-8xl sm:mx-0 sm:h-[32rem] xl:h-[36rem] 2xl:mx-auto 2xl:px-16">
      <div
        className={cx(
          'grid h-full w-full text-white',
          // [Key value propositions] + [headline + lead + address bar] + [trust vendor]
          'grid-rows-[4rem_12rem_4rem]',
          'sm:grid-cols-12 sm:grid-rows-[4rem_19rem_4rem]',
          'md:mt-12 lg:mt-24',
          'xl:grid-rows-[4rem_21rem_4rem]',
          'z-20',
        )}
      >
        {/* Key Value Propositions */}
        <div className="row-start-1 sm:col-span-6 sm:!col-start-2 md:col-span-8 2xl:!col-start-1">
          {keyValuePropositions?.length > 0 && (
            <dl
              className="flex items-start gap-6"
              aria-label={keyValuePropositionTitle}
            >
              {keyValuePropositions.map((proposition) => (
                <div key={proposition.title}>
                  <dt className="font-semibold text-shadow-2xs text-shadow-black">
                    {proposition.title}
                  </dt>
                  <dd className="text-xs font-medium text-shadow-2xs text-shadow-black">
                    {proposition.subtitle}
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </div>

        {/* Headline + Lead + Address search bar */}
        <div className="row-start-2 sm:col-span-6 sm:!col-start-2 md:col-span-5 2xl:!col-start-1">
          <h1 className="text-3xl font-semibold text-balance text-shadow-2xs text-shadow-black md:text-4xl lg:text-5xl">
            {headline}
          </h1>
          <div className="mt-2 overflow-hidden">
            {lead && (
              <PortablePTag
                value={lead}
                className="text-sm font-normal text-shadow-2xs text-shadow-black md:text-lg xl:text-xl"
              />
            )}
          </div>
          {/* Address Search Bar */}
          <div id="nav-scroll-trigger" className="mt-3 text-grey-10 sm:mt-6">
            {hasAddressBar && (
              <HeroAddressSearchBar
                mainHardwareType={mainHardwareType}
                description={heroSection.addressBarDescription}
              />
            )}
          </div>
        </div>

        {/* Trust Vendor */}
        <aside className="row-start-3 mt-16 self-start pb-2 sm:col-span-6 sm:!col-start-2 sm:mt-24 md:col-span-5 lg:mt-12 2xl:!col-start-1">
          {heroSection.trustVendor && (
            <TrustVendor {...heroSection.trustVendor} />
          )}
        </aside>
      </div>
    </div>
  );
};

export default DefaultContent;
